import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ActionButton from 'components-v2/atoms/ActionButton';
import { useCart } from 'context/Cart/CartContext';
import { useEffect, useRef } from 'react';
import { formatNumber } from 'utils/FormatNumber';
import { ImageFallbackStatic } from 'utils/ImageFallback';
import { useCartActions } from 'zustand-lib/useCart';
import ConsultantLogo from './ConsultantLogo';
import styles from './ConsultPopup.module.css';
import { useConsultPopupStore } from './ConsultPopup.store';

const MESSAGE_TYPES = {
  USER: 'user',
  CONSULTANT: 'consultant',
};

export default function ConsultPopup({ open = false, onClose, product }) {
  const { conversation, isDone, canChangeQty, message, setMessage, resetState, handleInitialConsultation, handleSendMessage, handleChangeQuantity, isLoading } =
    useConsultPopupStore();
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);

  // FIXME: merge useCart and useCartActions, so we only have to call one
  const { updateCart } = useCart();
  const { fetchCartDataByRouter } = useCartActions();

  useEffect(() => {
    if (open && product?.slug) {
      handleInitialConsultation(product);
    }
  }, [open, product?.slug, handleInitialConsultation]);

  // Auto scroll to bottom when conversation updates
  useEffect(() => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversation]);

  // Focus input after getting response (when isLoading changes from true to false)
  useEffect(() => {
    if (!isLoading && !isDone && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, isDone]);

  useEffect(() => {
    // reload the cart data after the conversation is done
    if (isDone && open) {
      updateCart();
      fetchCartDataByRouter();
    }
  }, [isDone]);

  const handleSendMessageLocal = async () => {
    if (!message.trim()) return;
    await handleSendMessage(product);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessageLocal();
    }
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={`consult-popup`}
      PaperProps={{
        style: {
          height: '80vh',
          maxHeight: '80vh',
          borderRadius: '1em',
        },
      }}
    >
      <DialogTitle className={`${styles.dialogTitle} consult-popup-header`}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <ConsultantLogo className={styles.consultantAvatar} />
            <Typography variant="h6" className={styles.headerTitle}>
              Buymed - Dược sĩ chuyên môn
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" className="consult-popup-close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={`${styles.dialogContent} consult-popup-body`}>
        {/* Conversation History */}
        <Box className={`${styles.conversationHistory} consult-popup-conversation`}>
          {/* Product Info as conversation bubble */}
          <ProductInfoBubble product={product} />

          {conversation.map((msg, index) => {
            // Check if this is the last message in a consecutive sequence of consultant messages
            const isLastInConsultantSequence =
              msg.type === MESSAGE_TYPES.CONSULTANT && (index === conversation.length - 1 || conversation[index + 1]?.type === MESSAGE_TYPES.USER);

            return (
              <Box
                key={msg.id}
                className={`${styles.messageContainer} ${msg.type === MESSAGE_TYPES.USER ? styles.userMessage : styles.consultantMessage} ${msg.type === MESSAGE_TYPES.USER ? 'consult-popup-user-message' : 'consult-popup-consultant-message'}`}
              >
                {msg.type === MESSAGE_TYPES.CONSULTANT && (
                  <div className={styles.consultantAvatarContainer}>
                    {isLastInConsultantSequence ? (
                      <ConsultantLogo className={styles.consultantAvatar} />
                    ) : (
                      <div className={styles.consultantAvatarPlaceholder} />
                    )}
                  </div>
                )}
                <div className={`${styles.message} ${msg.type === MESSAGE_TYPES.USER ? styles.userBubble : styles.consultantBubble}`}>
                  <Typography variant="body2" className={styles.messageText}>
                    <span dangerouslySetInnerHTML={{ __html: msg.content }} />
                  </Typography>
                  {(msg.type === MESSAGE_TYPES.USER || isLastInConsultantSequence) && (
                    <Typography variant="caption" className={styles.timestamp}>
                      {formatTime(msg.timestamp)}
                    </Typography>
                  )}
                </div>
              </Box>
            );
          })}
          <div ref={conversationEndRef} />
        </Box>

        {/* Input Area */}
        <Box className={`${styles.inputArea} consult-popup-input-area`}>
          {!isDone ? (
            <Box className={`${styles.inputContainer} consult-popup-input-container`}>
              <TextField
                type="number"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nhập số lượng sản phẩm bằng số. Ví dụ: 4"
                variant="outlined"
                fullWidth
                autoFocus
                disabled={isLoading}
                inputRef={inputRef}
                className={`${styles.textField} consult-popup-input`}
                inputProps={{
                  pattern: '[0-9]*',
                  inputMode: 'numeric',
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={handleSendMessageLocal}
                      disabled={!message.trim() || isLoading}
                      className={`${styles.sendIconButton} consult-popup-send-button`}
                    >
                      <SendIcon color="success" />
                    </IconButton>
                  ),
                }}
              />
            </Box>
          ) : (
            <Box className={`${styles.buttonContainer} consult-popup-button-container`}>
              {canChangeQty && (
                <ActionButton
                  size="full"
                  variant="outlined"
                  color="greenish"
                  onClick={() => handleChangeQuantity(product)}
                  disabled={isLoading}
                  className={`${styles.changeQuantityButton} consult-popup-change-quantity-button`}
                >
                  Thay đổi số lượng
                </ActionButton>
              )}
              <ActionButton
                size="full"
                variant="contained"
                color="greenish"
                onClick={handleClose}
                disabled={isLoading}
                className={`${styles.completeButton} consult-popup-complete-button`}
              >
                Hoàn tất
              </ActionButton>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function ProductInfoBubble({ product }) {
  const imageUrl = product?.imageUrls?.[0] || product.imageUrl || '';

  const productName = product?.productName || product?.name || 'Sản phẩm';

  let maxQuantity = product?.maxQuantity || product?.consumedMaxQuantity?.maxQuantityConsumed || 0;
  if (maxQuantity >= 10_000) {
    maxQuantity = 0;
  }

  return (
    <Box className={`${styles.messageContainer} ${styles.productInfoBubble} consult-popup-product-info`}>
      <div className={`${styles.message} ${styles.productInfoMessage}`}>
        <div className={styles.productInfoContent}>
          <div className={styles.productImageContainer}>
            <ImageFallbackStatic src={imageUrl} width={60} height={60} layout="fixed" className={styles.productImage} />
          </div>
          <div className={styles.productDetails}>
            <Typography variant="subtitle1" className={styles.productName}>
              {productName}
            </Typography>
            {!!maxQuantity && (
              <Typography variant="body2" className={styles.productLimit}>
                Đặt tối đa {formatNumber(maxQuantity)} sản phẩm
              </Typography>
            )}
          </div>
        </div>
      </div>
    </Box>
  );
}
